// Pure logic for deciding which columns/rows of a Genie query result are
// chartable. Kept dependency-free (no React/services) so it can be unit
// tested without pulling in the rest of GenieChat's import graph.

export type ChartType = 'bar' | 'pie' | 'line';

export interface ChartabilityResult {
  chartable: boolean;
  labelIdx: number;
  valueIndices: number[];
  suggestedType: ChartType;
  // Index of a second categorical column, when present (e.g. "Subcategory"
  // alongside a "Market" label column). Undefined for the normal single-
  // dimension case. When set, the chart should be a grouped/clustered bar
  // (one series per distinct value of this column) via pivotForGroupedChart.
  groupByIdx?: number;
}

// Turns a raw SQL column name into a human-readable axis/legend label,
// matching how Genie's own PNG titles its axes (e.g. the raw column "label"
// shows as "Label" in Genie's chart). Columns already written with spaces
// and capitals (e.g. "Allocation Amount") pass through unchanged.
export function humanizeColumnName(name: string): string {
  const spaced = name.includes('_') ? name.replace(/_/g, ' ') : name;
  return spaced.replace(/\w\S*/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
}

// "Nice numbers" axis tick algorithm (Heckbert), matching how Genie's own
// charts pick round steps (0/10/20/30 for a max of 28.10) instead of
// Recharts' default of scaling ticks to the raw data max (0/8/16/24/32).
export function computeNiceYAxisTicks(rawMax: number, targetTickCount = 5): { ticks: number[]; domainMax: number } {
  const max = rawMax > 0 ? rawMax : 1;
  const roughStep = max / Math.max(targetTickCount - 1, 1);
  const magnitude = 10 ** Math.floor(Math.log10(roughStep));
  const normalized = roughStep / magnitude;

  let niceNormalized: number;
  if (normalized < 1.5) niceNormalized = 1;
  else if (normalized < 3) niceNormalized = 2;
  else if (normalized < 7) niceNormalized = 5;
  else niceNormalized = 10;

  const step = niceNormalized * magnitude;
  const domainMax = Math.ceil(max / step) * step;

  const ticks: number[] = [];
  for (let t = 0; t <= domainMax + step / 2; t += step) {
    ticks.push(Math.round(t * 1e6) / 1e6); // avoid float drift like 20.000000001
  }

  return { ticks, domainMax };
}

// Per Akash (2026-09-15 meeting): a GROUP BY on Genie's own SQL is a
// stronger signal for "this should be a stacked bar chart" than counting
// unique values ourselves -- it reflects Genie's actual aggregation intent.
// Deliberately simple: just checks for the clause's presence, not which
// columns it groups by (Genie's generated SQL reliably groups by all
// selected categorical columns when there's a group-by dimension at all).
export function detectStackedFromSql(sql: string | undefined): boolean {
  if (!sql) return false;
  return /group\s+by/i.test(sql);
}

function toNumber(v: string): number {
  const n = Number(v);
  return Number.isNaN(n) ? 0 : n;
}

// Picks the numeric column with the widest range (max - min) as the sole
// series when candidate columns differ enough in scale that plotting them
// together would make the smaller one invisible (e.g. a 0-1 fraction next to
// a 0-100+ amount). Falls back to all candidates when scales are comparable.
function selectValueIndices(
  columns: { name: string }[],
  rows: string[][],
  candidateIndices: number[],
): number[] {
  if (candidateIndices.length <= 1) return candidateIndices;

  const ranges = candidateIndices.map(idx => {
    const values = rows.map(r => toNumber(r[idx]));
    const max = Math.max(...values);
    const min = Math.min(...values);
    return { idx, range: max - min || 1 };
  });

  const maxRange = Math.max(...ranges.map(r => r.range));
  const minRange = Math.min(...ranges.map(r => r.range));

  // Scales are "comparable" when the widest range isn't drastically bigger
  // than the narrowest -- an arbitrary but conservative 10x threshold.
  if (maxRange / minRange <= 10) return candidateIndices;

  const widest = ranges.reduce((a, b) => (b.range > a.range ? b : a));
  return [widest.idx];
}

export function detectChartability(columns: { name: string }[], rows: string[][]): ChartabilityResult {
  const empty: ChartabilityResult = { chartable: false, labelIdx: -1, valueIndices: [], suggestedType: 'bar' };
  if (!columns || columns.length < 2 || !rows || rows.length < 2) return empty;

  // Find categorical (non-numeric) columns and numeric value columns.
  // A result can have more than one categorical column (e.g. Market +
  // Subcategory) -- the first becomes the X-axis label, a second becomes
  // the grouping dimension for a clustered bar chart (see groupByIdx).
  let labelIdx = -1;
  let groupByIdx: number | undefined;
  const candidateValueIndices: number[] = [];

  columns.forEach((col, idx) => {
    // Check every row, not just the first 10 -- a result can have whole
    // categories with null values for this column (e.g. a subcategory with
    // no cost in any market), and if those happen to sort first, a
    // first-10 sample would wrongly conclude the column isn't numeric even
    // though most rows in the full result are.
    const allValues = rows.map(r => r[idx]);
    const nonNullValues = allValues.filter(v => v != null && v !== '' && v !== 'null');
    const numericCount = nonNullValues.filter(v => !Number.isNaN(Number(v))).length;
    // Judge numeric-ness against non-null values, not the raw row count --
    // a column that's genuinely numeric wherever it has data shouldn't be
    // penalized for being null in some rows (nulls are handled downstream
    // by toNumber defaulting to 0).
    const isNumeric = nonNullValues.length > 0 && numericCount > nonNullValues.length * 0.7;

    if (isNumeric) {
      candidateValueIndices.push(idx);
    } else if (labelIdx === -1) {
      labelIdx = idx;
    } else if (groupByIdx === undefined) {
      groupByIdx = idx;
    }
  });

  if (labelIdx === -1 || candidateValueIndices.length === 0) return empty;

  // With a group-by column, only one value column makes sense (the series
  // already come from the group-by values, not from multiple numeric
  // columns) -- use the first candidate and ignore selectValueIndices'
  // scale-comparison logic, which doesn't apply here.
  const valueIndices = groupByIdx !== undefined
    ? [candidateValueIndices[0]]
    : selectValueIndices(columns, rows, candidateValueIndices);

  // Rows are charted as Genie sent them -- including any total/summary row
  // it chose to include, since Genie's own PNG does the same. Only column
  // selection is adjusted (see selectValueIndices).
  const uniqueLabels = new Set(rows.map(r => r[labelIdx])).size;
  let suggestedType: ChartType = 'bar';
  if (uniqueLabels <= 6 && valueIndices.length === 1) suggestedType = 'pie';
  if (uniqueLabels > 12 || /date|month|year|quarter|period/.exec(columns[labelIdx].name.toLowerCase())) suggestedType = 'line';

  return { chartable: true, labelIdx, valueIndices, suggestedType, groupByIdx };
}

// Turns long-format rows (one row per label+group combination) into one row
// per distinct label value, with one field per distinct group value --
// exactly the shape Recharts' BarChart needs for a clustered/grouped bar
// chart. Missing label+group combinations default to 0 rather than being
// omitted, so every series has a bar (even if zero) for every label.
export function pivotForGroupedChart(
  columns: { name: string }[],
  rows: string[][],
  labelIdx: number,
  groupByIdx: number,
  valueIdx: number,
): { chartData: Record<string, string | number>[]; seriesNames: string[] } {
  const seriesNames = Array.from(new Set(rows.map(r => r[groupByIdx])));
  const labels = Array.from(new Set(rows.map(r => r[labelIdx])));

  const chartData = labels.map(label => {
    const entry: Record<string, string | number> = { label };
    seriesNames.forEach(series => {
      const match = rows.find(r => r[labelIdx] === label && r[groupByIdx] === series);
      entry[series] = match ? toNumber(match[valueIdx]) : 0;
    });
    return entry;
  });

  return { chartData, seriesNames };
}
